import { useCallback, useEffect, useReducer, useRef } from 'react';
import uuid from 'uuid';
import { ChatAction, ChatMessage, ChatState } from '../types';
import parseMessageSegments from '../parseMessageSegments';
import { AGENT_NAMES } from 'js-sdk/src/services/AgentCoreService';
import type { AgentName } from 'js-sdk/src/services/AgentCoreService';

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_USER_MESSAGE': {
      const msg: ChatMessage = {
        id: action.payload.id,
        role: 'user',
        content: action.payload.content,
        segments: [{ kind: 'text', content: action.payload.content }],
        timestamp: Date.now(),
      };
      return {
        ...state,
        messages: [...state.messages, msg],
        isLoading: true,
        error: null,
      };
    }
    case 'START_ASSISTANT_MESSAGE': {
      const lastMsg = state.messages[state.messages.length - 1];
      if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
        return state;
      }
      if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.isStreaming) {
        const messages = [...state.messages];
        messages[messages.length - 1] = { ...lastMsg, isStreaming: true };
        return { ...state, messages };
      }
      const msg: ChatMessage = {
        id: action.payload.id,
        role: 'assistant',
        content: '',
        segments: [],
        timestamp: Date.now(),
        isStreaming: true,
      };
      return {
        ...state,
        messages: [...state.messages, msg],
      };
    }
    case 'APPEND_TOKEN': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        const newContent = last.content + action.payload.text;
        messages[messages.length - 1] = {
          ...last,
          content: newContent,
          segments: parseMessageSegments(newContent),
        };
      } else if (last && last.role === 'assistant' && !last.isStreaming) {
        const newContent = last.content + action.payload.text;
        messages[messages.length - 1] = {
          ...last,
          content: newContent,
          segments: parseMessageSegments(newContent),
          isStreaming: true,
        };
      }
      return { ...state, messages };
    }
    case 'COMPLETE_ASSISTANT_MESSAGE': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        const finalContent = action.payload.text || last.content;
        messages[messages.length - 1] = {
          ...last,
          content: finalContent,
          segments: parseMessageSegments(finalContent),
          isStreaming: false,
        };
      }
      return { ...state, messages };
    }
    case 'STREAM_DONE': {
      return { ...state, isLoading: false };
    }
    case 'SET_ERROR': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        messages[messages.length - 1] = {
          ...last,
          isStreaming: false,
          error: action.payload.error,
        };
      }
      return { ...state, messages, isLoading: false, error: action.payload.error };
    }
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload.sessionId };
    case 'RESET':
      return createInitialState();
    default:
      return state;
  }
}

function createInitialState(): ChatState {
  return {
    messages: [],
    isLoading: false,
    sessionId: uuid.v4(),
    error: null,
  };
}

type OrgContext = {
  orgName: string;
  orgToken: string;
} | null;

export default function useWorkflowChat(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null, // TigerConnect SDK client
  orgContext?: OrgContext,
  agentName: AgentName = AGENT_NAMES.CLINICAL_WORKFLOW_CHAT
) {
  const [state, dispatch] = useReducer(chatReducer, undefined, createInitialState);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!client || !text.trim()) return;

      const userMsgId = uuid.v4();
      dispatch({ type: 'ADD_USER_MESSAGE', payload: { id: userMsgId, content: text.trim() } });

      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      const historyMessages = state.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }));

      const allMessages = [...historyMessages, { role: 'user' as const, content: text.trim() }];

      // On the first message, prepend org context so the agent skips asking for org name/token
      if (orgContext && historyMessages.length === 0) {
        allMessages.unshift({
          role: 'user' as const,
          content: `[System context — do not ask for these fields, they are already known] Organization name: ${orgContext.orgName}, Organization token: ${orgContext.orgToken}`,
        });
      }

      let streamDone = false;

      try {
        await client.agentCore.invoke(
          {
            agentName,
            messages: allMessages,
            stream: true,
            sessionId: state.sessionId,
            organizationId: orgContext?.orgToken,
            signal: abortRef.current.signal,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (eventData: { event: string; data: any }) => {
            const { event: eventType, data } = eventData;

            if (eventType === 'message_start') {
              if (data?.type === 'ai') {
                dispatch({
                  type: 'START_ASSISTANT_MESSAGE',
                  payload: { id: uuid.v4() },
                });
              }
            } else if (eventType === 'token') {
              dispatch({ type: 'APPEND_TOKEN', payload: { text: data.text } });
            } else if (eventType === 'message') {
              dispatch({
                type: 'COMPLETE_ASSISTANT_MESSAGE',
                payload: { text: data.text },
              });
            } else if (eventType === 'done') {
              streamDone = true;
              if (data?.session_id) {
                dispatch({
                  type: 'SET_SESSION_ID',
                  payload: { sessionId: data.session_id },
                });
              }
              dispatch({
                type: 'COMPLETE_ASSISTANT_MESSAGE',
                payload: { text: '' },
              });
              dispatch({ type: 'STREAM_DONE' });
            } else if (eventType === 'error') {
              dispatch({
                type: 'SET_ERROR',
                payload: { error: data.error || 'An error occurred' },
              });
            }
          }
        );
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // The stream may throw when the connection closes after a successful `done` event.
        // Ignore those transport-level errors since the conversation completed successfully.
        if (streamDone) return;
        const message = err instanceof Error ? err.message : 'Connection failed';
        dispatch({ type: 'SET_ERROR', payload: { error: message } });
      }
    },
    [agentName, client, orgContext, state.messages, state.sessionId]
  );

  const selectOption = useCallback(
    (value: string, label?: string) => {
      sendMessage(label || value);
    },
    [sendMessage]
  );

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    dispatch({ type: 'RESET' });
  }, []);

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    sendMessage,
    selectOption,
    reset,
  };
}
